import { RaceView } from '../game/view.js';
import { ticksToTimeString, TICK_RATE } from '@voidline/shared';
import { Settings } from '../persist.js';

/**
 * In-race HUD. Pure DOM (crisp text, zero canvas cost), values cached so the DOM
 * is only touched when something actually changes.
 */
export class Hud {
  root: HTMLDivElement;
  private el: Record<string, HTMLElement> = {};
  private cache: Record<string, string | number | boolean> = {};
  private minimapCtx: CanvasRenderingContext2D;
  private minimapPts: { x: number; y: number }[] = [];
  private minimapBounds = { minX: 0, maxX: 1, minZ: 0, maxZ: 1 };
  private messageTimer = 0;

  constructor(parent: HTMLElement, private settings: Settings) {
    this.root = document.createElement('div');
    this.root.id = 'hud';
    this.root.innerHTML = `
      <style>
        #hud { position: absolute; inset: 0; pointer-events: none; font-family: var(--font);
               --pad: calc(28px * var(--hud-scale)); }
        #hud .corner { position: absolute; }
        /* speed cluster */
        #hud-speed-wrap { right: var(--pad); bottom: var(--pad); text-align: right; }
        #hud-speed { font-size: calc(64px * var(--hud-scale)); font-weight: 200; letter-spacing: 0.04em;
                     color: #eef6ff; text-shadow: 0 1px 3px rgba(0,0,0,0.9), 0 0 18px rgba(49,200,255,0.5); font-variant-numeric: tabular-nums; line-height: 0.95; }
        #hud-speed-unit { font-size: calc(13px * var(--hud-scale)); color: var(--text-dim); letter-spacing: 0.3em; }
        #hud-boost { width: calc(220px * var(--hud-scale)); height: calc(10px * var(--hud-scale));
                     background: rgba(40,60,90,0.45); margin-top: 8px; margin-left: auto;
                     clip-path: polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%); position: relative; overflow: hidden; }
        #hud-boost > div { position: absolute; inset: 0 auto 0 0; background: linear-gradient(90deg, #1f7fae, #31c8ff, #8af3ff);
                     box-shadow: 0 0 12px rgba(49,200,255,0.8); }
        #hud-boost.full > div { background: linear-gradient(90deg, #ffd25e, #ffef9e); box-shadow: 0 0 14px rgba(255,210,94,0.9); }
        /* placement / lap */
        #hud-place-wrap { left: var(--pad); bottom: var(--pad); }
        #hud-place { font-size: calc(58px * var(--hud-scale)); font-weight: 200; color: #eef6ff; line-height: 0.9;
                     text-shadow: 0 1px 3px rgba(0,0,0,0.9), 0 0 18px rgba(49,200,255,0.4); }
        #hud-place sup { font-size: 0.4em; color: var(--text-dim); }
        #hud-lap { font-size: calc(15px * var(--hud-scale)); color: var(--text-dim); letter-spacing: 0.24em; margin-top: 6px;
                   text-shadow: 0 1px 3px rgba(0,0,0,0.9); }
        /* timers */
        #hud-times { left: 50%; transform: translateX(-50%); top: var(--pad); text-align: center;
                     font-variant-numeric: tabular-nums; }
        #hud-time { font-size: calc(26px * var(--hud-scale)); font-weight: 300; letter-spacing: 0.1em; color: #eef6ff;
                    text-shadow: 0 1px 3px rgba(0,0,0,0.9); }
        #hud-laptime { font-size: calc(14px * var(--hud-scale)); color: var(--text-dim); letter-spacing: 0.12em; margin-top: 2px;
                       text-shadow: 0 1px 3px rgba(0,0,0,0.9); }
        #hud-split { font-size: calc(15px * var(--hud-scale)); font-weight: 600; letter-spacing: 0.08em; margin-top: 4px; opacity: 0; transition: opacity 200ms; }
        #hud-split.show { opacity: 1; }
        #hud-split.ahead { color: var(--ok); }
        #hud-split.behind { color: var(--warn); }
        /* countdown */
        #hud-countdown { position: absolute; left: 50%; top: 34%; transform: translate(-50%, -50%);
                         font-size: calc(140px * var(--hud-scale)); font-weight: 100; color: #eef6ff; letter-spacing: 0.1em;
                         text-shadow: 0 0 60px rgba(49,200,255,0.8); opacity: 0; }
        #hud-countdown.pop { animation: cd-pop 900ms ease-out; }
        @keyframes cd-pop { 0% { opacity: 0; transform: translate(-50%,-50%) scale(1.6); }
                            18% { opacity: 1; transform: translate(-50%,-50%) scale(1); }
                            80% { opacity: 1; } 100% { opacity: 0; } }
        /* center messages */
        #hud-message { position: absolute; left: 50%; top: 46%; transform: translateX(-50%);
                       font-size: calc(30px * var(--hud-scale)); font-weight: 300; letter-spacing: 0.32em; color: #eef6ff;
                       text-shadow: 0 0 30px rgba(49,200,255,0.7); opacity: 0; transition: opacity 250ms; text-align:center; white-space: nowrap;}
        #hud-message.show { opacity: 1; }
        #hud-wrongway { position: absolute; left: 50%; top: 26%; transform: translateX(-50%);
                        font-size: calc(30px * var(--hud-scale)); font-weight: 700; letter-spacing: 0.3em; color: var(--warn);
                        text-shadow: 0 0 24px rgba(255,90,60,0.8); opacity: 0; }
        #hud-wrongway.show { opacity: 1; animation: ww-blink 700ms infinite; }
        @keyframes ww-blink { 50% { opacity: 0.25; } }
        /* minimap */
        #hud-minimap { right: var(--pad); top: var(--pad); opacity: 0.9; }
        #hud-minimap canvas { width: calc(170px * var(--hud-scale)); height: calc(170px * var(--hud-scale)); }
        /* standings mini-list */
        #hud-standings { left: var(--pad); top: var(--pad); font-size: calc(12px * var(--hud-scale));
                         letter-spacing: 0.1em; color: var(--text-dim); text-shadow: 0 1px 3px rgba(0,0,0,0.95);
                         background: rgba(4,7,12,0.35); padding: 8px 12px; }
        #hud-standings .row { display:flex; gap:8px; padding: 2px 0; }
        #hud-standings .row.me { color: #eef6ff; text-shadow: 0 0 8px rgba(49,200,255,0.6); }
        #hud-standings .pos { width: 1.4em; text-align: right; color: var(--accent); }
        /* elimination */
        #hud-elim { position: absolute; left: 50%; bottom: calc(var(--pad) + 20px); transform: translateX(-50%);
                    font-size: calc(16px * var(--hud-scale)); letter-spacing: 0.2em; color: var(--accent2);
                    text-shadow: 0 0 14px rgba(255,45,111,0.7); opacity: 0; }
        #hud-elim.show { opacity: 1; }
        /* net indicator */
        #hud-net { position: absolute; right: var(--pad); top: calc(var(--pad) + 180px * var(--hud-scale) + 10px);
                   font-size: 11px; color: var(--text-dim); font-variant-numeric: tabular-nums; text-align: right; letter-spacing: 0.08em;}
        #hud-net.bad { color: var(--warn); }
        #hud-boostlabel { font-size: calc(10px * var(--hud-scale)); letter-spacing: 0.34em; color: var(--text-dim); margin-top: 4px; }
      </style>
      <div class="corner" id="hud-standings"></div>
      <div class="corner" id="hud-times">
        <div id="hud-time">0:00.000</div>
        <div id="hud-laptime">LAP 0:00.000 · BEST --:--.---</div>
        <div id="hud-split"></div>
      </div>
      <div class="corner" id="hud-minimap"><canvas width="340" height="340"></canvas></div>
      <div class="corner" id="hud-place-wrap">
        <div id="hud-place">1<sup>st</sup></div>
        <div id="hud-lap">LAP 1/3</div>
      </div>
      <div class="corner" id="hud-speed-wrap">
        <div id="hud-speed">0</div>
        <div id="hud-speed-unit">KM/H</div>
        <div id="hud-boost"><div style="width:50%"></div></div>
        <div id="hud-boostlabel">BOOST</div>
      </div>
      <div id="hud-countdown"></div>
      <div id="hud-message"></div>
      <div id="hud-wrongway">WRONG WAY</div>
      <div id="hud-elim"></div>
      <div id="hud-net"></div>
    `;
    parent.appendChild(this.root);
    for (const id of [
      'hud-speed', 'hud-speed-unit', 'hud-boost', 'hud-place', 'hud-lap', 'hud-time', 'hud-laptime',
      'hud-split', 'hud-countdown', 'hud-message', 'hud-wrongway', 'hud-elim', 'hud-net', 'hud-standings',
    ]) {
      this.el[id] = this.root.querySelector(`#${id}`)!;
    }
    this.minimapCtx = (this.root.querySelector('#hud-minimap canvas') as HTMLCanvasElement).getContext('2d')!;
  }

  private set(id: string, text: string): void {
    if (this.cache[id] !== text) {
      this.cache[id] = text;
      this.el[id].textContent = text;
    }
  }

  prepareTrack(view: RaceView): void {
    // project track onto 2D for the minimap
    this.minimapPts = [];
    const sp = view.track.main;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (let i = 0; i < sp.count; i += 2) {
      const x = sp.pos[i * 3];
      const z = sp.pos[i * 3 + 2];
      this.minimapPts.push({ x, y: z });
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
    }
    const padX = (maxX - minX) * 0.1 + 1;
    const padZ = (maxZ - minZ) * 0.1 + 1;
    this.minimapBounds = { minX: minX - padX, maxX: maxX + padX, minZ: minZ - padZ, maxZ: maxZ + padZ };
  }

  showMessage(text: string, seconds = 2.2): void {
    this.el['hud-message'].textContent = text;
    this.el['hud-message'].classList.add('show');
    this.messageTimer = seconds;
  }

  showCountdown(text: string): void {
    const el = this.el['hud-countdown'];
    el.textContent = text;
    el.classList.remove('pop');
    void (el as HTMLElement).offsetWidth; // restart animation
    el.classList.add('pop');
  }

  update(view: RaceView, dt: number): void {
    const local = view.local;
    const speedShip = view.localIndex >= 0 ? view.ships[view.localIndex] : view.ships[view.standings[0] ?? 0];

    // speed
    if (speedShip) {
      const kmh = Math.round(speedShip.speed * 3.6);
      this.set('hud-speed', String(this.settings.speedUnits === 'kmh' ? kmh : Math.round(speedShip.speed)));
      this.set('hud-speed-unit', this.settings.speedUnits === 'kmh' ? 'KM/H' : 'VL');
      // boost bar
      const pct = Math.round(speedShip.boostEnergy * 100);
      if (this.cache['boost'] !== pct) {
        this.cache['boost'] = pct;
        (this.el['hud-boost'].firstElementChild as HTMLElement).style.width = `${pct}%`;
        this.el['hud-boost'].classList.toggle('full', pct > 96);
      }
    }

    if (local) {
      // placement
      const place = local.placement;
      const suffix = place === 1 ? 'st' : place === 2 ? 'nd' : place === 3 ? 'rd' : 'th';
      if (this.cache['place'] !== place) {
        this.cache['place'] = place;
        this.el['hud-place'].innerHTML = `${place}<sup>${suffix}</sup>`;
      }
      this.set(
        'hud-lap',
        view.mode === 'sprint'
          ? `SPRINT · CP ${local.nextCheckpoint}/${local.checkpointCount}`
          : `LAP ${local.lap}/${local.totalLaps}`
      );
      this.set('hud-time', ticksToTimeString(Math.floor(local.raceTimeTicks)));
      this.set(
        'hud-laptime',
        `LAP ${ticksToTimeString(Math.max(0, Math.floor(local.currentLapTicks)))} · BEST ${
          local.bestLapTicks > 0 ? ticksToTimeString(local.bestLapTicks) : '--:--.---'
        }`
      );

      // split delta
      const split = this.el['hud-split'];
      if (local.splitDeltaMs !== null) {
        const ahead = local.splitDeltaMs <= 0;
        split.textContent = `${ahead ? '−' : '+'}${(Math.abs(local.splitDeltaMs) / 1000).toFixed(2)}`;
        split.classList.add('show');
        split.classList.toggle('ahead', ahead);
        split.classList.toggle('behind', !ahead);
      } else {
        split.classList.remove('show');
      }

      this.el['hud-wrongway'].classList.toggle('show', local.wrongWay);
    }

    // countdown digits handled by events; message timer
    if (this.messageTimer > 0) {
      this.messageTimer -= dt;
      if (this.messageTimer <= 0) this.el['hud-message'].classList.remove('show');
    }

    // standings list
    const key = view.standings.join(',') + view.ships.map((s) => s.lap).join('.');
    if (this.cache['standings'] !== key) {
      this.cache['standings'] = key;
      let html = '';
      view.standings.forEach((idx, i) => {
        const s = view.ships[idx];
        if (!s) return;
        const me = idx === view.localIndex;
        const flag = s.eliminated && !s.finished ? ' ✕' : s.finished ? ' ✓' : '';
        html += `<div class="row${me ? ' me' : ''}"><span class="pos">${i + 1}</span><span>${escapeHtml(
          s.info.name
        )}${flag}</span></div>`;
      });
      this.el['hud-standings'].innerHTML = html;
    }

    // elimination timer
    const elim = this.el['hud-elim'];
    if (view.eliminationIn !== null && view.eliminationIn < 900) {
      elim.textContent = `ELIMINATION IN ${Math.ceil(view.eliminationIn)}`;
      elim.classList.add('show');
    } else {
      elim.classList.remove('show');
    }

    // net indicator
    const net = this.el['hud-net'];
    if (view.net) {
      const bad = view.net.ping > 150 || view.net.packetsPerSec < 12;
      this.set('hud-net', `${view.net.ping}ms · ${view.net.packetsPerSec}pk/s${bad ? ' ⚠' : ''}`);
      net.classList.toggle('bad', bad);
    } else {
      this.set('hud-net', '');
    }

    this.drawMinimap(view);
  }

  private drawMinimap(view: RaceView): void {
    const ctx = this.minimapCtx;
    const W = 340, H = 340;
    ctx.clearRect(0, 0, W, H);
    const b = this.minimapBounds;
    const sx = (x: number) => ((x - b.minX) / (b.maxX - b.minX)) * (W - 20) + 10;
    const sy = (z: number) => ((z - b.minZ) / (b.maxZ - b.minZ)) * (H - 20) + 10;

    ctx.strokeStyle = 'rgba(120,170,220,0.5)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    this.minimapPts.forEach((p, i) => {
      if (i === 0) ctx.moveTo(sx(p.x), sy(p.y));
      else ctx.lineTo(sx(p.x), sy(p.y));
    });
    if (view.track.main.closed) ctx.closePath();
    ctx.stroke();

    // ships
    for (let i = view.standings.length - 1; i >= 0; i--) {
      const idx = view.standings[i];
      const s = view.ships[idx];
      if (!s || !s.visible) continue;
      const me = idx === view.localIndex;
      ctx.fillStyle = me ? '#31c8ff' : s.info.isBot ? 'rgba(200,215,235,0.65)' : '#ff2d6f';
      ctx.beginPath();
      ctx.arc(sx(s.pos.x), sy(s.pos.z), me ? 7 : 5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  setVisible(v: boolean): void {
    this.root.style.display = v ? '' : 'none';
  }

  dispose(): void {
    this.root.remove();
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
